import { useMemo } from 'react'
import * as Turf from '@turf/turf'
import GeoJSON from 'geojson'
import { Point } from 'mapbox-gl'

import { AnyAirspace } from '@/domain/models/Airspace'

import { getAirspaceLabelStackId, getAirspaceStackId } from '../../containers/MapStackPresenter'
import { MapGeoJsonLayer } from '../MapView/MapGeoJsonLayer'
import { MapGeoJsonSource } from '../MapView/MapGeoJsonSource'
import { MapSymbolLayer } from '../MapView/MapSymbolLayer'
import { MapSymbolSource } from '../MapView/MapSymbolSource'
import { AIRSPACE_SYMBOL_IMAGES, getAirspaceDisplay } from './airspaceTypeRegistry'
import { buildAirspaceCodeLabel } from './buildAirspaceCodeLabel'

import { MetricsEvents } from '@/constants/metricsEvents'
import { sendMetrics } from '@/presentation/utils/sendMetrics'

export interface MapAirspaceProps {
  airspaceId: string
  borderSourceId: string
  borderLayerId: string
  symbolSourceId: string
  symbolLayerId: string
  features: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>
  activeAirspace?: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>
  onPress?: (feature: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>, point: Point) => void
}

function MapAirspace(props: MapAirspaceProps) {
  const { airspaceId, borderLayerId, borderSourceId, symbolLayerId, symbolSourceId, features, onPress } = props

  const display = getAirspaceDisplay(airspaceId)

  const symbolFeatureCollection: GeoJSON.FeatureCollection<GeoJSON.Point, AnyAirspace> = useMemo(() => {
    const symbolFeatures: Array<GeoJSON.Feature<GeoJSON.Point, AnyAirspace>> = []

    const visited: { [key: string]: number } = {}

    for (const feature of features.features) {
      let center
      if (!!feature.properties.labelPosLat && !!feature.properties.labelPosLng) {
        center = Turf.point([feature.properties.labelPosLng, feature.properties.labelPosLat], {
          ...feature.properties,
          codeLabel: buildAirspaceCodeLabel(feature.properties?.code),
          textSize: airspaceId === 'fis' ? 11 : 12,
          padding: airspaceId === 'fis' ? [6, 6, 6, 6] : [3, 6, 3, 6]
        })
      } else {
        center = Turf.centerOfMass(feature, {
          properties: {
            ...feature.properties,
            codeLabel: buildAirspaceCodeLabel(feature.properties?.code),
            textSize: airspaceId === 'fis' ? 11 : 12,
            padding: airspaceId === 'fis' ? [6, 6, 6, 6] : [3, 6, 3, 6]
          }
        })

        const separationDecimals = display.labelSeparationDecimals
        const key = `${center.geometry.coordinates[0].toFixed(
          separationDecimals
        )}-${center.geometry.coordinates[1].toFixed(separationDecimals)}`
        const timesVisited = visited[key]

        if (display.labelYOffset) {
          center.geometry.coordinates = Turf.destination(center.geometry.coordinates, display.labelYOffset, 0, {
            units: 'degrees'
          }).geometry.coordinates
        }

        if (timesVisited > 0) {
          visited[key]++

          center.geometry.coordinates = Turf.destination(
            center.geometry.coordinates,
            0.25 * (timesVisited % 2 === 0 ? Math.floor(timesVisited / 2) : Math.floor(-timesVisited / 2)),
            0,
            {
              units: 'degrees'
            }
          ).geometry.coordinates
        } else {
          visited[key] = 1
        }
      }

      symbolFeatures.push(center as GeoJSON.Feature<GeoJSON.Point, AnyAirspace>)
    }

    return {
      type: 'FeatureCollection',
      features: symbolFeatures
    }
  }, [features])

  return (
    <>
      <MapGeoJsonSource id={borderSourceId} data={features}>
        <MapGeoJsonLayer
          types={['line']}
          id={borderLayerId}
          sourceId={borderSourceId}
          beforeLayerId={getAirspaceStackId(airspaceId)}
          borderColor={display.color}
          borderWidth={['interpolate', ['linear'], ['zoom'], 0, 0.5, 10, 5, 12, 20, 22, 25]}
          borderOpacity={1}
          onBorderClick={(features: GeoJSON.Feature[], point: Point) => {
            onPress?.(features[0], point)
          }}
        />
      </MapGeoJsonSource>

      <MapSymbolSource id={symbolSourceId} data={symbolFeatureCollection}>
        <MapSymbolLayer
          id={symbolLayerId}
          sourceId={symbolSourceId}
          beforeLayerId={getAirspaceLabelStackId(airspaceId)}
          imageSrc={AIRSPACE_SYMBOL_IMAGES[display.symbol]}
          layout={{
            'text-field': ['get', 'codeLabel'],
            'text-size': ['get', 'textSize'],
            'text-font': ['Open Sans Bold'],
            'icon-text-fit': 'both',
            'icon-text-fit-padding': ['get', 'padding'],
            'icon-allow-overlap': true,
            'text-ignore-placement': true,
            'text-allow-overlap': true,
            'text-anchor': 'center'
          }}
          iconColor={display.color}
          textColor={display.textColor}
          minZoomLevel={display.labelMinLevel}
          onSymbolClick={(e) => {
            sendMetrics(MetricsEvents.MAP_AIRSPACES_SELECTED)
            onPress?.(e.features[0], e.point)
          }}
        />
      </MapSymbolSource>
    </>
  )
}

export { MapAirspace }
